// S11-6: Alerts list page, D1-backed and org-scoped.
//
// Tabs (Active Alerts / Alert Rules) are URL-driven — /alerts vs
// /alerts/rules — so the browser Back button + deep links both work.
// Active-tab filters live in the query string so a user sharing the
// URL hands someone else the same view.
//
// Filter state is derived from URL search params (no mirrored React
// state). That keeps URL and UI in lockstep and means reload keeps
// filters intact.
//
// Row actions (vista#608 — ack-only lifecycle):
//   - Acknowledge is the SINGLE manual action: one click, optimistic, no
//     confirmation. It transitions active → acknowledged, which removes the
//     alert from the default active view — i.e. ack is terminal for the
//     operator. There is no manual Resolve button anymore.
//   - Resolution is engine-owned: the agent auto-resolves an alert (→ resolved)
//     when the underlying condition clears. Acknowledged/resolved rows show a
//     status label instead of an action.
//
// Empty states (AC S11-6):
//   - No rules configured:   NoRulesEmptyState (CTA → /alerts/rules)
//   - Rules exist, 0 active: NoActiveAlertsEmptyState + optional
//     "Last alert resolved <relative>" when resolved-in-24h exists.
//
// Hostname resolution pulls from the selected-agent devices cache
// (already populated by other pages); alerts with no cache hit show
// the raw device_id.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useSearchParams, Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import { useAuth } from '../hooks/useAuth.js';
import { useAgentStore } from '../store/agentStore.jsx';
import { useDevices } from '../hooks/useAgent.js';
import { displayName } from '../lib/deviceDisplay.js';
import {
  useAlertsList,
  useAcknowledgeAlert,
  useMostRecentResolvedAlert,
  useBulkResolveAlerts,
} from '../hooks/useAlertsApi.js';
import ConfirmDialog from '../components/shared/ConfirmDialog.jsx';
import StatusBadge from '../components/shared/StatusBadge.jsx';
import PrimaryButton from '../components/shared/PrimaryButton.jsx';
import FilterChips from '../components/alerts/FilterChips.jsx';
import {
  NoActiveAlertsEmptyState,
  NoRulesEmptyState,
} from '../components/alerts/AlertsEmptyState.jsx';
import AlertRuleManager from '../components/alerts/AlertRuleManager.jsx';

import RelativeTime from '../components/shared/RelativeTime.jsx';
import { TableSkeleton } from '../components/shared/Skeleton.jsx';
// Parse URL params → filter object. Missing params fall back to the
// defaults (state=active, everything else null).
//
// device_id is parseable here so the DeviceDetail page's
// "View resolved alerts" link (S11-7) can deep-link a device-scoped
// resolved list at /alerts?device_id=...&state=resolved.
function filtersFromSearchParams(sp) {
  const state = sp.get('state') || 'active';
  const severity = sp.get('severity') || null;
  const agent_id = sp.get('agent_id') || null;
  const device_id = sp.get('device_id') || null;
  return { state, severity, agent_id, device_id };
}

// Serialize filters back into URL params. null values are dropped so
// the URL stays clean (?state=active rather than
// ?state=active&severity=&agent_id=).
function filtersToSearchParams(filters) {
  const next = new URLSearchParams();
  if (filters.state && filters.state !== 'active') next.set('state', filters.state);
  if (filters.severity) next.set('severity', filters.severity);
  if (filters.agent_id) next.set('agent_id', filters.agent_id);
  if (filters.device_id) next.set('device_id', filters.device_id);
  return next;
}

export default function Alerts() {
  const location = useLocation();
  const isRulesTab = location.pathname.startsWith('/alerts/rules');
  return isRulesTab ? <AlertsRulesTab /> : <ActiveAlertsTab />;
}

function TabSwitcher({ isRulesTab }) {
  const { role } = useAuth();
  const canManageRules = role === 'org_admin';
  return (
    <div className="flex items-center justify-between">
      <h1 className="text-2xl font-bold text-gray-900">Alerts</h1>
      {canManageRules && (
        <div className="inline-flex rounded-md border border-gray-300 bg-white" role="tablist">
          <Link
            to="/alerts"
            role="tab"
            aria-selected={!isRulesTab}
            className={`px-4 py-1.5 text-sm font-medium first:rounded-l-md ${
              !isRulesTab ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Active Alerts
          </Link>
          <Link
            to="/alerts/rules"
            role="tab"
            aria-selected={isRulesTab}
            className={`px-4 py-1.5 text-sm font-medium last:rounded-r-md ${
              isRulesTab ? 'bg-blue-600 text-white' : 'text-gray-600 hover:bg-gray-50'
            }`}
          >
            Alert Rules
          </Link>
        </div>
      )}
    </div>
  );
}

function AlertsRulesTab() {
  const { role } = useAuth();
  // RequireAuth isn't applied at the route level (the two tabs share
  // one path prefix), so gate in-component. Non-admins landing here
  // (e.g. via a shared URL) see the Active Alerts view instead.
  if (role !== 'org_admin') {
    return (
      <div className="space-y-4">
        <TabSwitcher isRulesTab={false} />
        <p className="text-sm text-gray-500">
          You need organization admin access to manage alert rules.
        </p>
      </div>
    );
  }
  return (
    <div className="space-y-4">
      <TabSwitcher isRulesTab={true} />
      <AlertRuleManager />
    </div>
  );
}

function ActiveAlertsTab() {
  const [searchParams, setSearchParams] = useSearchParams();
  const filters = useMemo(() => filtersFromSearchParams(searchParams), [searchParams]);

  const { data: alerts, isLoading, error } = useAlertsList(filters);

  // Rules existence drives the empty-state choice — if the list is
  // empty AND there are no rules, the user should see the "configure
  // rules" CTA rather than the "happy path" resolved-banner state.
  const { agentId } = useAgentStore();
  const { data: rules } = useQuery({
    queryKey: ['alert-rules', agentId],
    queryFn: () => api.get('/alert-rules', { agent_id: agentId }),
    enabled: !!agentId,
    // #676: /alert-rules now returns the cursor envelope
    // {data,cursor,error}. Unwrap to the array (the established
    // `resp?.data ?? resp` pattern; the ?? fallback keeps a bare-array
    // response working too). This view only needs "any rules exist?", so
    // the first page is sufficient — no cursor follow required.
    select: (resp) => {
      const items = resp?.data ?? resp;
      return Array.isArray(items) ? items : [];
    },
  });
  const hasRules = Array.isArray(rules) ? rules.length > 0 : false;

  // Device-name resolution: pull the currently-selected agent's
  // device list from the shared cache. Alerts for other agents (or
  // devices not in the page-1 slice) just render the raw device_id.
  const { data: devicesResp } = useDevices(undefined, { limit: 500 });
  const devicesById = useMemo(() => {
    const list = Array.isArray(devicesResp?.data)
      ? devicesResp.data
      : Array.isArray(devicesResp)
        ? devicesResp
        : [];
    const map = new Map();
    for (const d of list) {
      if (d.device_id) map.set(d.device_id, d);
    }
    return map;
  }, [devicesResp]);

  function updateFilters(next) {
    setSearchParams(filtersToSearchParams(next), { replace: true });
  }

  // Friendly device label. Precedence:
  //   1. alert.device_name — frozen at fire time by the agent (alias →
  //      hostname → auto_hostname). Works cross-agent and in resolved
  //      history even if the device is later deleted.
  //   2. the selected agent's device cache via displayName() — for older
  //      alerts persisted before the agent embedded device_name.
  //   3. short device_id — last resort, never the full 36-char UUID.
  function deviceLabel(alert) {
    if (!alert.device_id) return '—';
    if (alert.device_name && alert.device_name.trim()) return alert.device_name;
    const d = devicesById.get(alert.device_id);
    if (d) return displayName(d);
    return alert.device_id.slice(0, 8);
  }

  const { data: lastResolved } = useMostRecentResolvedAlert();
  const ackMutation = useAcknowledgeAlert();
  const { role } = useAuth();
  const bulkResolve = useBulkResolveAlerts();
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);

  const list = Array.isArray(alerts) ? alerts : [];
  const isActiveView = filters.state === 'active';
  // vista#629: bulk "resolve all" is an org_admin cleanup tool; only offer it
  // when looking at an open view (active/acknowledged) that actually has rows.
  const canBulkResolve = role === 'org_admin'
    && list.length > 0
    && filters.state !== 'resolved';

  return (
    <div className="space-y-4">
      <TabSwitcher isRulesTab={false} />

      <div className="flex items-center justify-between gap-3">
        <FilterChips filters={filters} onChange={updateFilters} />
        {canBulkResolve && (
          <button
            type="button"
            onClick={() => setBulkConfirmOpen(true)}
            className="shrink-0 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
          >
            Resolve all
          </button>
        )}
      </div>

      {isLoading ? (
        <TableSkeleton rows={8} cols={5} message="Loading alerts…" />
      ) : error ? (
        // vista#259: when /api/v1/alerts errors (e.g. unapplied D1 migration
        // surfacing as 500), don't fall through to the empty-state path —
        // that misleads operators into seeing "No alert rules" while the
        // data layer is actually broken. Surface the error explicitly.
        <div className="rounded-md border border-red-200 bg-red-50 p-6 text-center">
          <p className="text-sm font-medium text-red-800">
            Failed to load alerts
          </p>
          <p className="mt-1 text-xs text-red-700">
            {error?.body?.error || error?.message || 'Unknown error'}
          </p>
          <p className="mt-3 text-xs text-red-600">
            Try refreshing. If the problem persists, contact your administrator.
          </p>
        </div>
      ) : list.length === 0 && isActiveView ? (
        hasRules ? (
          <NoActiveAlertsEmptyState lastResolvedAt={lastResolved?.resolved_at} />
        ) : (
          <NoRulesEmptyState />
        )
      ) : list.length === 0 ? (
        <div className="py-12 text-center text-sm text-gray-400">
          No alerts match the current filters.
        </div>
      ) : (
        <AlertsTable
          alerts={list}
          deviceLabel={deviceLabel}
          onAcknowledge={(id) => ackMutation.mutate(id)}
          ackPendingId={ackMutation.isPending ? ackMutation.variables : null}
          isActiveView={isActiveView}
        />
      )}

      <ConfirmDialog
        open={bulkConfirmOpen}
        title="Resolve all alerts?"
        body={
          <>
            This resolves <strong>all open (active + acknowledged) alerts</strong>
            {(filters.severity || filters.agent_id || filters.device_id) ? ' matching the current filters' : ' for this organization'}.
            It's a one-time cleanup — the engine still auto-resolves and raises new
            alerts as conditions change.
          </>
        }
        confirmLabel="Resolve all"
        confirmVariant="destructive"
        loading={bulkResolve.isPending}
        onCancel={() => setBulkConfirmOpen(false)}
        onConfirm={() => {
          bulkResolve.mutate(
            { severity: filters.severity, agent_id: filters.agent_id, device_id: filters.device_id },
            { onSuccess: () => setBulkConfirmOpen(false) },
          );
        }}
      />
    </div>
  );
}

// S14-8 (#496 review B1+B2): pure keyboard-dispatch helper. Maps a raw
// `KeyboardEvent` + the relevant table state to an `action` object the
// useEffect consumer applies. Factoring it out lets the keyboard
// contract be unit-tested without jsdom; this file's existing tests
// are SSR-only and can't fire interactive events. The first version
// of this had Shift+A as dead code (e.key === 'a' && e.shiftKey is
// unreachable because browsers set e.key to the shifted character
// 'A'); the export + tests below pin the correct behavior.
//
// Returns null when the event should be ignored. The 'preventDefault'
// flag tells the consumer whether to call e.preventDefault().
//
// Form-field guard: when the event originates in an INPUT/TEXTAREA/
// contentEditable, return null so search boxes and other text fields
// aren't hijacked.
export function dispatchAlertsKeyDown(e, ctx) {
  const target = e.target;
  const tag = target?.tagName;
  if (tag === 'INPUT' || tag === 'TEXTAREA' || target?.isContentEditable) {
    return null;
  }
  if (e.key === 'ArrowDown') {
    return { kind: 'focus-delta', delta: 1, preventDefault: true };
  }
  if (e.key === 'ArrowUp') {
    return { kind: 'focus-delta', delta: -1, preventDefault: true };
  }
  // Shift+A — ack all visible. Must precede the plain-A arm; both
  // lower- and upper-case key values are accepted because non-US
  // layouts vary in which one they emit when Shift is held.
  if (e.shiftKey && (e.key === 'A' || e.key === 'a')) {
    return {
      kind: 'ack',
      alertIds: [...ctx.visibleActiveIds],
      preventDefault: true,
    };
  }
  if (e.key === 'a' || e.key === 'A') {
    if (
      ctx.focusedIndex >= 0 &&
      ctx.focusedIndex < ctx.activeAlerts.length
    ) {
      return {
        kind: 'ack',
        alertIds: [ctx.activeAlerts[ctx.focusedIndex].alert_id],
        preventDefault: true,
      };
    }
    return null;
  }
  if (e.key === ' ' || e.code === 'Space') {
    if (
      ctx.focusedIndex >= 0 &&
      ctx.focusedIndex < ctx.activeAlerts.length
    ) {
      return {
        kind: 'select-toggle',
        alertId: ctx.activeAlerts[ctx.focusedIndex].alert_id,
        preventDefault: true,
      };
    }
    return null;
  }
  if (e.key === 'Escape') {
    return { kind: 'clear', preventDefault: false };
  }
  return null;
}

// S14-8 vista#465: AlertsTable extended with keyboard navigation +
// multi-select + bulk-ack toolbar. The three friction-point fixes from
// the audit: (1) prominent primary-styled ack button on every active
// row, (2) `A` / `Shift+A` keyboard shortcuts (with on-screen help),
// (3) checkbox column + sticky toolbar for bulk-ack across selected
// rows.
// Map an alert's rule_type to a human source category for an at-a-glance
// badge. rule_type reliably encodes the source: agent threshold/status
// rules emit `health_*` / `device_down`, and syslog signature alerts
// carry a `syslog:` prefix (the agent's "syslog:{name}" rule). 'Down'
// and 'Health' auto-resolve when the condition clears; 'Log' alerts stay
// active until an operator clears them.
// #742: fabric alerts (proxy/uplink/agent/host) aren't about a device — the
// Subject column shows a kind badge + short ref instead of a device link.
// Module-level (pure) so both Alerts and AlertsTable can use them.
function isFabricAlert(alert) {
  return alert.subject_kind && alert.subject_kind !== 'device';
}
function subjectRefLabel(alert) {
  if (!alert.subject_ref) return '—';
  // Refs are UUIDs (proxy_id / target_id / agent_id) — show a short prefix;
  // the full ref is in the title attr and the human name is in the message.
  return alert.subject_ref.length > 12 ? alert.subject_ref.slice(0, 8) : alert.subject_ref;
}

function alertSource(ruleType) {
  const t = (ruleType || '').toLowerCase();
  if (t.startsWith('syslog')) {
    return { label: 'Log', cls: 'bg-purple-50 text-purple-700 ring-purple-600/20', sticky: true };
  }
  // #563 review: today the agent's rule_name_to_type only emits
  // `device_down` for this category; `status_change`/`absence` are
  // forward-looking (the agent's RuleCondition supports them, but no
  // built-in rule maps to those rule_types yet) — kept so a future rule
  // lands in the right badge without a dashboard change.
  if (t === 'device_down' || t.startsWith('status_change') || t.startsWith('absence')) {
    return { label: 'Down', cls: 'bg-red-50 text-red-700 ring-red-600/20', sticky: false };
  }
  if (t.startsWith('health')) {
    return { label: 'Health', cls: 'bg-amber-50 text-amber-700 ring-amber-600/20', sticky: false };
  }
  // #633: informational "new device discovered" alerts. The agent emits
  // `device_discovered` (RuleCondition::DeviceNew); they auto-resolve after a
  // short TTL, so non-sticky.
  if (t === 'device_discovered' || t.startsWith('device_new')) {
    return { label: 'New', cls: 'bg-sky-50 text-sky-700 ring-sky-600/20', sticky: false };
  }
  // S15-NS #606: network-service health alerts (agent emits `fabric:service`).
  // Distinct "Service" badge; the Subject column links to ServiceDetail.
  if (t === 'fabric:service') {
    return { label: 'Service', cls: 'bg-teal-50 text-teal-700 ring-teal-600/20', sticky: false };
  }
  // #742: fabric-health alerts — proxy-offline / uplink-down (agent emits
  // `fabric:{kind}`) and agent-offline (cloud cron emits `agent_offline`).
  // These auto-resolve when the link/target recovers, so non-sticky.
  if (t.startsWith('fabric') || t === 'agent_offline') {
    return { label: 'Fabric', cls: 'bg-indigo-50 text-indigo-700 ring-indigo-600/20', sticky: false };
  }
  return { label: 'Other', cls: 'bg-gray-50 text-gray-600 ring-gray-500/20', sticky: false };
}

function AlertsTable({
  alerts,
  deviceLabel,
  onAcknowledge,
  ackPendingId,
  isActiveView,
}) {
  const { role } = useAuth();
  // Either org role can ack/resolve — writes are gated at the API by
  // withAuth('org_ro') (alerts routes allow reads AND these state
  // transitions at org_ro, per S11-4).
  const canAct = role === 'org_admin' || role === 'org_ro';

  // Multi-select state. Cleared whenever the alert list refreshes —
  // selecting an alert ID that's now resolved would be a no-op anyway
  // but keeps the toolbar honest.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  // Keyboard-focus row index. -1 means no row focused; users can also
  // click into a row to set focus (cell click handler below).
  const [focusedIndex, setFocusedIndex] = useState(-1);
  const tableRef = useRef(null);

  // Reset selection when the underlying alert list changes (filter
  // switch, new fires landing, etc.). Avoids stale "Ack selected (5)"
  // claims after the user navigates the URL.
  useEffect(() => {
    setSelectedIds(new Set());
    setFocusedIndex(-1);
  }, [alerts]);

  const activeAlerts = useMemo(
    () => alerts.filter((a) => a.state === 'active'),
    [alerts],
  );

  // Visible-active = "currently rendered + still active". `A` /
  // `Shift+A` operate on this set so a partial filter (e.g. severity
  // = critical) bounds the bulk action to what the operator sees.
  const visibleActiveIds = useMemo(() => activeAlerts.map((a) => a.alert_id), [activeAlerts]);

  // Toggle one row's selection.
  const toggleSelected = (alertId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(alertId)) next.delete(alertId);
      else next.add(alertId);
      return next;
    });
  };

  // Select-all toggles the visible-active set.
  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      const allSelected = visibleActiveIds.every((id) => prev.has(id));
      if (allSelected) return new Set();
      return new Set(visibleActiveIds);
    });
  };

  const bulkAckSelected = () => {
    for (const id of selectedIds) {
      onAcknowledge(id);
    }
    setSelectedIds(new Set());
  };

  // Keyboard shortcuts (S14-8 UX-fix 2):
  //   ArrowUp/ArrowDown : move row focus within the active-alerts list
  //   A                 : ack the currently focused row
  //   Shift+A           : ack ALL currently visible active rows
  //   Space             : toggle the focused row's selection checkbox
  //   Escape            : clear focus + selection
  // Disabled when the focus is inside a form input (text/textarea) so
  // search inputs and other text fields aren't hijacked.
  //
  // S14-8 (#496 review B2): the dispatch logic is factored into the
  // pure `dispatchAlertsKeyDown` helper below so the keyboard contract
  // can be unit-tested without jsdom. The useEffect just wires it up.
  useEffect(() => {
    if (!canAct || !isActiveView) return undefined;
    const onKeyDown = (e) => {
      const action = dispatchAlertsKeyDown(e, {
        activeAlerts,
        focusedIndex,
        visibleActiveIds,
      });
      if (!action) return;
      if (action.preventDefault) e.preventDefault();
      switch (action.kind) {
        case 'focus-delta':
          setFocusedIndex((i) =>
            Math.max(0, Math.min(activeAlerts.length - 1, i + action.delta)),
          );
          break;
        case 'ack':
          for (const id of action.alertIds) onAcknowledge(id);
          break;
        case 'select-toggle':
          toggleSelected(action.alertId);
          break;
        case 'clear':
          setFocusedIndex(-1);
          setSelectedIds(new Set());
          break;
        default:
          break;
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [canAct, isActiveView, activeAlerts, focusedIndex, visibleActiveIds, onAcknowledge]);

  const selectionCount = selectedIds.size;
  const allActiveSelected =
    visibleActiveIds.length > 0 && visibleActiveIds.every((id) => selectedIds.has(id));

  return (
    <>
      {/* S14-8 UX-fix 3: sticky bulk-ack toolbar. Renders only when
          at least one row is selected; hugs the top of the scroll
          container so the action stays reachable while scrolling. */}
      {canAct && isActiveView && selectionCount > 0 && (
        <div className="sticky top-0 z-10 mb-2 flex items-center justify-between rounded-md border border-blue-200 bg-blue-50 px-4 py-2 shadow-sm">
          <span className="text-sm font-medium text-blue-900">
            {selectionCount} alert{selectionCount === 1 ? '' : 's'} selected
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setSelectedIds(new Set())}
              className="text-xs text-blue-700 hover:underline"
            >
              Clear
            </button>
            <PrimaryButton onClick={bulkAckSelected} className="px-3 py-1 text-sm">
              Ack selected ({selectionCount})
            </PrimaryButton>
          </div>
        </div>
      )}

      {/* Keyboard-shortcut help strip. Visible only when canAct +
          active-view because the shortcuts themselves are gated the
          same way. */}
      {canAct && isActiveView && activeAlerts.length > 0 && (
        <p className="mb-2 text-xs text-gray-500">
          Shortcuts:{' '}
          <kbd className="rounded border border-gray-300 bg-gray-50 px-1 font-mono">↑↓</kbd> move focus,{' '}
          <kbd className="rounded border border-gray-300 bg-gray-50 px-1 font-mono">A</kbd> ack focused,{' '}
          <kbd className="rounded border border-gray-300 bg-gray-50 px-1 font-mono">Shift+A</kbd> ack all visible,{' '}
          <kbd className="rounded border border-gray-300 bg-gray-50 px-1 font-mono">Space</kbd> toggle select,{' '}
          <kbd className="rounded border border-gray-300 bg-gray-50 px-1 font-mono">Esc</kbd> clear
        </p>
      )}

      <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white" ref={tableRef}>
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {canAct && isActiveView && (
                <th className="w-8 px-2 py-3 text-left text-xs font-medium uppercase text-gray-500">
                  <input
                    type="checkbox"
                    aria-label="Select all visible active alerts"
                    checked={allActiveSelected}
                    onChange={toggleSelectAll}
                    className="rounded border-gray-300"
                  />
                </th>
              )}
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Severity</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Source</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Subject</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Message</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">Opened</th>
              <th className="px-4 py-3 text-left text-xs font-medium uppercase text-gray-500">State</th>
              {canAct && (
                <th className="px-4 py-3 text-right text-xs font-medium uppercase text-gray-500">Actions</th>
              )}
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {alerts.map((a) => {
              const isActive = a.state === 'active';
              const activeIdx = isActive ? activeAlerts.findIndex((x) => x.alert_id === a.alert_id) : -1;
              const isFocused = activeIdx >= 0 && activeIdx === focusedIndex;
              return (
                <tr
                  key={a.alert_id}
                  onClick={() => isActive && activeIdx >= 0 && setFocusedIndex(activeIdx)}
                  className={
                    isFocused
                      ? 'bg-blue-50 ring-1 ring-inset ring-blue-300'
                      : 'hover:bg-gray-50'
                  }
                >
                  {canAct && isActiveView && (
                    <td className="w-8 px-2 py-3">
                      {isActive && (
                        <input
                          type="checkbox"
                          aria-label={`Select alert ${a.alert_id}`}
                          checked={selectedIds.has(a.alert_id)}
                          onChange={() => toggleSelected(a.alert_id)}
                          onClick={(e) => e.stopPropagation()}
                          className="rounded border-gray-300"
                        />
                      )}
                    </td>
                  )}
                  <td className="px-4 py-3"><StatusBadge status={a.severity} /></td>
                  {/* Source — at-a-glance origin (Health/Down/Log); the
                      specific rule_type sits beneath as the detailed cause. */}
                  <td className="px-4 py-3 text-sm">
                    {(() => {
                      const s = alertSource(a.rule_type);
                      return (
                        <>
                          <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ring-1 ring-inset ${s.cls}`}>
                            {s.label}
                          </span>
                          <span className="mt-0.5 block truncate text-xs text-gray-400" title={a.rule_type || a.rule_name || ''}>
                            {a.rule_type || a.rule_name || ''}
                          </span>
                        </>
                      );
                    })()}
                  </td>
                  {/* Subject — a device (friendly name, click-through) or, for
                      #742 fabric alerts, a proxy/uplink/agent kind badge. */}
                  <td className="px-4 py-3 text-sm">
                    {isFabricAlert(a) ? (
                      <span className="inline-flex items-center gap-1.5">
                        <span className="rounded bg-gray-100 px-1.5 py-0.5 text-xs font-medium capitalize text-gray-600">
                          {a.subject_kind}
                        </span>
                        {/* S15-NS #606: service subjects link to ServiceDetail
                            (subject_ref = service_id). */}
                        {a.subject_kind === 'service' && a.subject_ref ? (
                          <Link
                            to={`/services/${a.subject_ref}`}
                            onClick={(e) => e.stopPropagation()}
                            className="font-mono text-xs text-blue-700 hover:underline"
                            title={a.subject_ref}
                          >
                            {subjectRefLabel(a)}
                          </Link>
                        ) : (
                          <span className="font-mono text-xs text-gray-500" title={a.subject_ref || ''}>
                            {subjectRefLabel(a)}
                          </span>
                        )}
                      </span>
                    ) : a.device_id ? (
                      <Link
                        // vista#589: Alerts is org-wide (cross-agent), so the
                        // device may live under a different agent than the one
                        // selected. Carry the alert's agent_id so DeviceDetail
                        // adopts it instead of 404'ing under the selected agent.
                        to={`/devices/${a.device_id}${a.agent_id ? `?agent_id=${encodeURIComponent(a.agent_id)}` : ''}`}
                        onClick={(e) => e.stopPropagation()}
                        className="font-medium text-blue-700 hover:underline"
                      >
                        {deviceLabel(a)}
                      </Link>
                    ) : (
                      <span className="text-gray-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 max-w-md">
                    <span className="block truncate" title={a.message}>{a.message || '—'}</span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                    <RelativeTime ts={a.triggered_at} />
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={a.state === 'active' ? 'critical' : a.state === 'acknowledged' ? 'warning' : 'inactive'} label={a.state} /></td>
                  {canAct && (
                    <td className="px-4 py-3 text-right">
                      <AlertRowActions
                        alert={a}
                        onAcknowledge={onAcknowledge}
                        ackPending={ackPendingId === a.alert_id}
                      />
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function AlertRowActions({ alert, onAcknowledge, ackPending }) {
  // vista#608: ack-only lifecycle. Acknowledge is the single manual action and
  // is terminal for the operator (the alert leaves the active view); the agent
  // auto-resolves it when the underlying condition clears. There is no manual
  // Resolve. Acknowledged/resolved rows show a status label, not an action.
  if (alert.state === 'resolved') {
    return <span className="text-xs text-gray-400">Resolved</span>;
  }
  if (alert.state === 'acknowledged') {
    return <span className="text-xs text-gray-500">Acknowledged</span>;
  }
  return (
    <PrimaryButton
      loading={ackPending}
      onClick={() => onAcknowledge(alert.alert_id)}
      className="px-4 py-1.5 text-sm font-medium"
    >
      <span className="inline-flex items-center gap-1.5">
        <svg className="h-3.5 w-3.5" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="3">
          <path d="M5 10l3.5 3.5L15 7" />
        </svg>
        Acknowledge
      </span>
    </PrimaryButton>
  );
}
